import React from 'react';
import { PageHeader } from '@/components/ui/page-header';
import { Container } from '@/components/ui/container';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <Container className="py-8 max-w-4xl">
        <PageHeader 
          heading="Privacy Policy" 
          subheading="GentleBirth LLC Privacy Policy - Effective for Sleepnest.app"
        />
        
        <main className="mt-8 prose prose-slate max-w-none dark:prose-invert">
          <section className="mb-8">
            <p className="text-muted-foreground">
              Welcome to GentleBirth LLC's ("GentleBirth") website located at http://www.GentleBirth.app 
              and GentleBirth.com (the "Website"), the mobile software application "GentleBirth" (the "App") 
              accessed via the Web Portal and/or a mobile or other device interface, the Knowledge Base, 
              the GentleBirth Blog, GentleBirth social media sites and any other GentleBirth online interfaces 
              and properties owned and/or managed by GentleBirth ("Sites"). These Terms of Use govern your 
              use of all Sites, all content, data, and information provided on and/or through the Sites, 
              and any products and services offered by GentleBirth and purchased through the Sites 
              (collectively the "Products and Services").
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Important Medical Disclaimer</h2>
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-800 dark:text-red-200 font-semibold mb-2">MEDICAL DISCLAIMER:</p>
              <p className="text-red-800 dark:text-red-200">
                THE PRODUCTS AND SERVICES ARE PROVIDED FOR INFORMATIONAL PURPOSES ONLY AND ARE NOT 
                INTENDED AS MEDICAL ADVICE OR AS A SUBSTITUTE FOR THE PROFESSIONAL SERVICES OF A 
                LICENSED PHYSICIAN OR OTHER HEALTH CARE PROVIDER OR PRACTITIONER. GENTLEBIRTH DOES 
                NOT PROVIDE MEDICAL ADVICE AND IS NOT A LICENSED HEALTH CARE PROVIDER.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">User Responsibility</h2>
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-amber-800 dark:text-amber-200 mb-2">
                EACH USER OF THE SERVICE (A "USER") USES THE PRODUCTS AND SERVICES AT THE USER'S SOLE RISK. 
                This means that:
              </p>
              <ul className="text-amber-800 dark:text-amber-200 list-disc list-inside space-y-2">
                <li>A USER MAY OR MAY NOT SUCCESSFULLY DISCOVER A PATTERN THAT LEADS TO A DIAGNOSIS OF A POTENTIAL ILLNESS OR DISEASE WITH THE USE OF THE PRODUCTS AND SERVICES.</li>
                <li>ALL INFORMATION IS INPUT BY YOU AND YOU ARE SOLELY RESPONSIBLE FOR THE INTERPRETATION OF YOUR INFORMATION.</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              These Terms of Use, including the Introduction and Background Information set forth above 
              (the "Terms"), govern your use of the Products and Services, whether the Products and Services 
              are provided through the Website by GENTLEBIRTH, or the App.
            </p>
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
              <p className="text-blue-800 dark:text-blue-200 font-semibold">
                PLEASE READ THESE TERMS CAREFULLY AS THEY CREATE A BINDING LEGAL CONTRACT BETWEEN YOU AND GENTLEBIRTH.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Eligibility and Access</h2>
            <p className="text-muted-foreground mb-4">
              You represent and warrant that you are at least the age of majority in the jurisdiction 
              where you are located (which is 18 years of age in most locations in the United States) 
              and fully able, competent and authorized to enter into and be bound by these Terms.
            </p>
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200 font-semibold">
                NOTE TO CHILDREN UNDER 13 YEARS OF AGE: THE SERVICE IS NOT FOR PERSONS UNDER THE AGE OF 13.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Privacy Policy Integration</h2>
            <p className="text-muted-foreground">
              Your privacy is important to GENTLEBIRTH. The GENTLEBIRTH Privacy Policy is incorporated 
              into these Terms of Use by reference. Please read the Privacy Policy carefully for details 
              relating to the collection, use, and disclosure of information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Clinical Studies and Data Collection</h2>
            <p className="text-muted-foreground">
              GENTLEBIRTH may conduct clinical studies at any time based on the GENTLEBIRTH app, or any 
              other products and services provided by GENTLEBIRTH. By downloading, using, or purchasing 
              any of GENTLEBIRTHs products and/or services, you consent to being contacted by GENTLEBIRTH 
              as a potential participant in any clinical study(-ies) GENTLEBIRTH plans to conduct.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">User License Grant</h2>
            <p className="text-muted-foreground mb-4">
              Subject to the obligation to maintain the confidentiality of personally identifiable information 
              about a User as identified in the Privacy Policy, a User of the Products and Services grants 
              GENTLEBIRTH and its affiliates a non-exclusive, fully paid-up, royalty-free, perpetual, 
              irrevocable, sublicensable, and transferable license to use Anonymous Personal Information for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Improving the Products and Services or the functionality of the Website or App</li>
              <li>Responding to User inquiries or technological issues</li>
              <li>Aggregated research and marketing purposes, including scientific research</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Payment and Subscriptions</h2>
            <h3 className="text-lg font-semibold mb-2">General Payment Terms</h3>
            <p className="text-muted-foreground mb-4">
              All or portions of the Products and Services may be provided for free or for a recurring 
              subscription fee, or for a stated product price. You will be notified in advance in the 
              App before signing up for any portion of the Service for which a recurring subscription 
              fee is charged.
            </p>
            
            <h3 className="text-lg font-semibold mb-2">Free Trial Period</h3>
            <p className="text-muted-foreground mb-4">
              Certain fee-based portions of the Service may be available for a one-time free trial period. 
              Unless you terminate your Trial Period prior to the end of such Trial Period, your credit 
              card will be charged the recurring subscription fee applicable at the time you signed up.
            </p>

            <h3 className="text-lg font-semibold mb-2">Cancellation and Refunds</h3>
            <p className="text-muted-foreground">
              You may cancel your subscription at any time by visiting your iTunes Account or Google Play 
              Account to manage subscription settings. However, all payments are final and there are no 
              refunds offered for the early termination of any period for which you have paid a subscription fee.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Intellectual Property Rights</h2>
            <p className="text-muted-foreground">
              The Website, the App and the Products and Services are owned and operated by GENTLEBIRTH. 
              The content, visual interfaces, interactive features, information, graphics, design, compilation, 
              computer code, products, software, hardware, services, and all other elements are protected by 
              copyright, trade dress, patent, and trademark laws of the United States, Canada and other 
              jurisdictions, international conventions, and all other relevant intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">User Content and Restrictions</h2>
            <p className="text-muted-foreground mb-4">
              If you submit, upload or post any comments, ideas, suggestions, information, files, images 
              or other materials ("User Generated Content") to us, you agree not to provide any User 
              Generated Content that:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Is defamatory, abusive, libelous, unlawful, obscene, threatening, harassing, fraudulent, pornographic, or harmful</li>
              <li>Violates or infringes the privacy, copyright, trademark, trade dress, trade secrets or intellectual property rights</li>
              <li>Contains or transmits a virus or any other harmful component</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Prohibited Uses</h2>
            <p className="text-muted-foreground mb-4">
              As a condition of your use of the Products and Services, you agree not to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Use the service for any unlawful purpose or in violation of these Terms</li>
              <li>Reproduce, duplicate, copy, sell, trade, resell, or distribute any portion of the service</li>
              <li>Interfere with or damage the operation of the service</li>
              <li>Attempt to gain unauthorized access to the service or other user accounts</li>
              <li>Use automated means to access the service without permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Account Security</h2>
            <p className="text-muted-foreground">
              When you register as a User you will be asked to create a password for your account. You are 
              solely responsible for maintaining the confidentiality of your account and password, and for 
              restricting access to your computer or any device on which you use the App. You agree to accept 
              responsibility for all activities that occur under your account or password.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Third-Party Content and Links</h2>
            <p className="text-muted-foreground">
              GENTLEBIRTH or third parties may provide links on and through the Website, or the App to 
              third-party websites ("Reference Sites"). GENTLEBIRTH has no control over such Reference 
              Sites and therefore makes no claim or representation regarding their accuracy, quality, 
              efficacy, legality, nature, availability or reliability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Disclaimers and Limitations</h2>
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <p className="font-semibold mb-2">IMPORTANT DISCLAIMERS:</p>
              <ul className="text-muted-foreground space-y-2">
                <li>• The Products and Services are provided "AS IS" and "AS AVAILABLE"</li>
                <li>• GENTLEBIRTH disclaims all warranties, express or implied</li>
                <li>• No warranty that the service will be uninterrupted or error-free</li>
                <li>• Limitation of liability for any damages arising from use of the service</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Indemnification</h2>
            <p className="text-muted-foreground">
              You agree to indemnify and hold harmless GENTLEBIRTH, and its parent, subsidiaries, affiliates, 
              suppliers, licensors and partners, and their officers, directors, employees, agents and 
              representatives from any and all claims, losses, damages, liabilities, costs or expenses 
              arising out of your use of the Products and Services, your violation of these Terms, or 
              your violation of the rights of any other person or entity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Termination</h2>
            <p className="text-muted-foreground mb-4">
              GENTLEBIRTH may terminate your account at any time for your violation of any of the provisions 
              of these Terms or your failure to pay subscription fees. You may terminate these Terms at any 
              time by closing your account and discontinuing your use of the service.
            </p>
            <p className="text-muted-foreground">
              Upon termination, your access to your Personal Information shall immediately terminate and you 
              are solely responsible for maintaining copies of any Personal Information you have uploaded.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Digital Millennium Copyright Act (DMCA)</h2>
            <p className="text-muted-foreground mb-4">
              Information in the notification is accurate, and under penalty of perjury, that the complaining party is authorized to act on behalf of the owner of an exclusive right that is allegedly infringed (for example, "I swear, under penalty of perjury, that the information in this notification is accurate and that I am the copyright owner, or authorized to act on behalf of the copyright owner, of the copyright(s) that is/are allegedly infringed by the aforementioned content."). Please consult your legal counsel or see 17 U.S.C. § 512 to confirm these requirements.
            </p>
            
            <h3 className="text-lg font-semibold mb-2">Termination of User Accounts for Infringement</h3>
            <p className="text-muted-foreground">
              GENTLEBIRTH does not tolerate infringing activities and infringement of intellectual property rights on or through the use of the Website or the App, and GENTLEBIRTH will remove all content if properly notified that such content infringes on another's intellectual property rights, including copyrights and trademarks. It is GENTLEBIRTH policy to respond promptly to notices of alleged infringement that comply with the DMCA. In addition, GENTLEBIRTH will promptly terminate without notice the accounts of Users, that are determined by GENTLEBIRTH to be "repeat infringers." A repeat infringer is a User who has been notified of infringing activity more than twice and/or has had content removed from the Website or the App more than twice. Any other feedback, comments, requests for technical support or other communications should be directed to GENTLEBIRTH customer service through team@GENTLEBIRTH.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">United States Export Controls</h2>
            <p className="text-muted-foreground">
              You agree to comply with all export laws and restrictions and regulations of the United States Department of Commerce or other United States or other sovereign agency or authority when ordering, receiving, or using the Website, the App or the Service, and not to export, or allow the export or re-export of any technical data, software, hardware, or any direct product of any of the foregoing products and services, in violation of any such restrictions, laws or regulations, or unless and until all required licenses and authorizations are obtained with respect to the countries specified in the applicable United States Export Administration Regulations (or any successor supplement or regulations). The transfer of certain technical data, hardware, software, and commodities may require a license from an agency of the United States government and/or written assurances by you that you will not export such technical data or commodities to certain foreign countries without prior approval of such agency. Your rights under these Terms are contingent on your compliance with this provision.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Miscellaneous</h2>
            
            <h3 className="text-lg font-semibold mb-2">Notice</h3>
            <p className="text-muted-foreground mb-4">
              GENTLEBIRTH may provide you with notices, including those regarding changes to these Terms, by postings on the Website or the App, via electronic mail, or by postal delivery. Notice sent via e-mail will be deemed given twenty-four hours after such e-mail is sent, unless GENTLEBIRTH is notified that the electronic mail address is invalid. Alternatively, we may give you legal notice by mail to a postal address, if provided by you. In such case, notice will be deemed given three days after the date of mailing. Notice given via the Website or the App will be deemed given upon the earlier of 5 days following posting on the Website or the App or when first reviewed by you.
            </p>

            <h3 className="text-lg font-semibold mb-2">Dispute Resolution</h3>
            <p className="text-muted-foreground mb-4">
              If a dispute arises between you and GENTLEBIRTH, our goal is to provide you with a neutral and cost effective means of resolving the dispute quickly. Accordingly, you and GENTLEBIRTH agree that we will resolve any claim or controversy at law or equity that arises out of these Terms (a "Claim") in accordance with one of the subsections below or as GENTLEBIRTH and you otherwise agree in writing. Before resorting to these alternatives, we strongly encourage you to first contact us directly to seek a resolution.
            </p>

            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Choice of Law; Forum</h4>
              <p className="text-blue-800 dark:text-blue-200">
                These Terms shall be governed in all respects by the laws of the State of California and the United States of America. You agree that any claim or dispute you may have against GENTLEBIRTH must be resolved by a court located in Santa Clara CA, except as otherwise agreed by you and GENTLEBIRTH or as described in the Arbitration Option paragraph below.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Other Provisions</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
              <li>A provision of these Terms may be waived only by a written instrument executed by the party entitled to the benefit of such provision</li>
              <li>The Terms and related Guidelines, and any rights and licenses granted hereunder, may not be transferred or assigned by you without GENTLEBIRTH's prior written consent</li>
              <li>No joint venture, partnership, employment, or agency relationship exists between you and GENTLEBIRTH as a result of these Terms</li>
              <li>This is the entire agreement between you and GENTLEBIRTH relating to the subject matter herein</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">GDPR/CCPA Policies and Other Privacy Laws</h2>
            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-200">
                Refer to the following documents: Cookies Policy & Privacy Notice
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
              <p className="text-muted-foreground mb-2">
                For questions about this Privacy Policy or our Terms of Use, please contact us at:
              </p>
              <p className="font-semibold">Team@GENTLEBIRTH.COM</p>
              <p className="text-muted-foreground mt-2">
                The products and services hereunder are offered by GENTLEBIRTH, LLC. You can contact GentleBirth LLC. by electronic mail at team@GENTLEBIRTH.com.
              </p>
            </div>
          </section>

          <section className="text-sm text-muted-foreground border-t pt-4">
            <p>Last updated: {new Date().toLocaleDateString()}</p>
            <p className="mt-2">
              This Privacy Policy applies to Sleepnest.app and is governed by GentleBirth LLC's terms and conditions.
            </p>
          </section>
        </main>
      </Container>
    </div>
  );
}